import React, { useState, useEffect, useRef } from 'react';

const SceneExplorerAdmin = ({ API, adminToken, showNotification, isDarkMode }) => {
  const [activeTab, setActiveTab] = useState('scenes');
  const [scenes, setScenes] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selectedScene, setSelectedScene] = useState(null);
  const [sceneObjects, setSceneObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSceneModal, setShowSceneModal] = useState(false);
  const [showObjectModal, setShowObjectModal] = useState(false);
  const [editingScene, setEditingScene] = useState(null);
  const [editingObject, setEditingObject] = useState(null);
  const [resetConfirm, setResetConfirm] = useState(null); // { sceneId, sceneName, dependentScenes }
  const [confirmModal, setConfirmModal] = useState(null); // { title, message, confirmText, confirmColor, onConfirm }
  const imageRef = useRef(null);

  const headers = { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' };

  useEffect(() => { loadScenes(); loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API}/admin/scene-explorer/settings`, { headers });
      if (res.ok) setSettings(await res.json());
    } catch (e) { console.error('Error loading settings:', e); }
  };

  const loadScenes = async () => {
    try {
      const res = await fetch(`${API}/admin/scene-explorer/scenes`, { headers });
      if (res.ok) setScenes(await res.json());
    } catch (e) { console.error('Error loading scenes:', e); }
    finally { setLoading(false); }
  };

  const loadSceneObjects = async (sceneId) => {
    try {
      const res = await fetch(`${API}/admin/scene-explorer/scenes/${sceneId}/objects`, { headers });
      if (res.ok) setSceneObjects(await res.json());
    } catch (e) { console.error('Error loading objects:', e); }
  };

  const saveSettings = async (updates) => {
    try {
      const res = await fetch(`${API}/admin/scene-explorer/settings`, {
        method: 'PUT', headers, body: JSON.stringify(updates)
      });
      if (res.ok) { setSettings(prev => ({ ...prev, ...updates })); showNotification('Settings saved', 'success'); }
    } catch (e) { showNotification('Error saving settings', 'error'); }
  };

  const saveScene = async (sceneData) => {
    try {
      const isEdit = !!editingScene?.id;
      const url = isEdit ? `${API}/admin/scene-explorer/scenes/${editingScene.id}` : `${API}/admin/scene-explorer/scenes`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST', headers, body: JSON.stringify(sceneData)
      });
      if (res.ok) {
        showNotification(isEdit ? 'Scene updated' : 'Scene created', 'success');
        loadScenes();
        // If we edited the scene that's currently open in the editor, update it
        if (isEdit && selectedScene?.id === editingScene.id) {
          setSelectedScene(prev => ({ ...prev, ...sceneData }));
        }
        setShowSceneModal(false);
        setEditingScene(null);
      } else {
        const err = await res.json().catch(() => ({}));
        showNotification(err.detail || `Error ${res.status}: Failed to save scene`, 'error');
      }
    } catch (e) {
      console.error('Save scene error:', e);
      showNotification('Network error saving scene: ' + e.message, 'error');
    }
  };

  const deleteScene = async (sceneId, skipConfirm = false) => {
    if (!skipConfirm) {
      setConfirmModal({
        title: 'Delete Scene',
        message: 'Delete this scene and all its objects? This cannot be undone.',
        confirmText: 'Delete',
        confirmColor: 'red',
        onConfirm: () => { setConfirmModal(null); deleteScene(sceneId, true); }
      });
      return;
    }
    try {
      const res = await fetch(`${API}/admin/scene-explorer/scenes/${sceneId}`, { method: 'DELETE', headers });
      if (res.ok) {
        showNotification('Scene deleted', 'success');
        loadScenes();
        if (selectedScene?.id === sceneId) { setSelectedScene(null); setSceneObjects([]); }
      }
    } catch (e) { showNotification('Error deleting scene', 'error'); }
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API}/admin/scene-explorer/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        showNotification('Image uploaded', 'success');
        return data.url;
      } else {
        const err = await res.json();
        showNotification(err.detail || 'Upload failed', 'error');
      }
    } catch (e) { showNotification('Upload error', 'error'); }
    return null;
  };

  const handleImageClick = (e) => {
    if (!selectedScene || !imageRef.current) return;
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const labels = {};
    (settings?.languages || []).forEach(l => { labels[l.code] = ''; });
    setEditingObject({ position_x: Math.round(x * 10) / 10, position_y: Math.round(y * 10) / 10, labels });
    setShowObjectModal(true);
  };

  const saveObject = async (objData) => {
    try {
      const isEdit = !!editingObject?.id;
      const url = isEdit
        ? `${API}/admin/scene-explorer/objects/${editingObject.id}`
        : `${API}/admin/scene-explorer/scenes/${selectedScene.id}/objects`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST', headers, body: JSON.stringify(objData)
      });
      if (res.ok) {
        showNotification(isEdit ? 'Object updated' : 'Object added', 'success');
        loadSceneObjects(selectedScene.id);
        setShowObjectModal(false);
        setEditingObject(null);
      }
    } catch (e) { showNotification('Error saving object', 'error'); }
  };

  const deleteObject = async (objectId, skipConfirm = false) => {
    if (!skipConfirm) {
      setConfirmModal({
        title: 'Delete Object',
        message: 'Delete this object marker? This cannot be undone.',
        confirmText: 'Delete',
        confirmColor: 'red',
        onConfirm: () => { setConfirmModal(null); deleteObject(objectId, true); }
      });
      return;
    }
    try {
      const res = await fetch(`${API}/admin/scene-explorer/objects/${objectId}`, { method: 'DELETE', headers });
      if (res.ok) {
        showNotification('Object deleted', 'success');
        loadSceneObjects(selectedScene.id);
      }
    } catch (e) { showNotification('Error deleting object', 'error'); }
  };

  const cardClass = isDarkMode ? 'bg-gray-800 rounded-2xl border border-gray-700' : 'bg-white rounded-2xl border border-gray-200 shadow-sm';
  const inputClass = `w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-purple-500`;

  // Reorder scene: swap with adjacent and bulk-save all order values
  const moveScene = async (sceneId, direction) => {
    const idx = scenes.findIndex(s => s.id === sceneId);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= scenes.length) return;
    // Build new order: swap the two scenes, then send full ID list
    const reordered = [...scenes];
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];
    const scene_ids = reordered.map(s => s.id);
    try {
      await fetch(`${API}/admin/scene-explorer/scenes/reorder`, {
        method: 'POST', headers, body: JSON.stringify({ scene_ids })
      });
      loadScenes();
    } catch (e) { showNotification('Error reordering', 'error'); }
  };

  // Resolve image URL — handles local paths (/api/static/...) and full URLs (Dropbox etc.)
  const resolveImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      // Convert Dropbox share links to direct image links
      return url.replace('?dl=0', '?raw=1').replace('www.dropbox.com', 'dl.dropboxusercontent.com');
    }
    return `${API.replace('/api', '')}${url}`;
  };

  // ── Settings Tab ──
  const renderSettings = () => (
    <div className={`${cardClass} p-6`}>
      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Feature Settings</h3>
      <div className="space-y-4">
        {/* Game Card Text */}
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
          <p className={`font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>🏷️ Game Card Text</p>
          <div className="space-y-3">
            <div>
              <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Game Name</label>
              <input
                value={settings?.name || ''}
                onChange={e => setSettings(prev => ({ ...prev, name: e.target.value }))}
                className={`w-full px-3 py-1.5 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'} border focus:border-blue-500 focus:outline-none`}
              />
            </div>
            <div>
              <label className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Description</label>
              <input
                value={settings?.description || ''}
                onChange={e => setSettings(prev => ({ ...prev, description: e.target.value }))}
                className={`w-full px-3 py-1.5 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'} border focus:border-blue-500 focus:outline-none`}
              />
            </div>
            <button
              onClick={() => saveSettings({ name: settings?.name, description: settings?.description })}
              className="px-4 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-semibold"
            >
              Save Card Text
            </button>
          </div>
        </div>

        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={settings?.is_visible ?? true}
            onChange={(e) => saveSettings({ is_visible: e.target.checked })}
            className="w-5 h-5 rounded" />
          <div>
            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Visible to Users</p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Show Scene Explorer in games menu</p>
          </div>
        </label>
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={settings?.is_premium ?? false}
            onChange={(e) => saveSettings({ is_premium: e.target.checked })}
            className="w-5 h-5 rounded" />
          <div>
            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Premium Only (Global)</p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Require Pro plan for entire feature (individual scenes can also be set to premium)</p>
          </div>
        </label>
      </div>
    </div>
  );

  // Open the visual editor for a scene
  const openSceneEditor = (scene) => {
    setSelectedScene(scene);
    loadSceneObjects(scene.id);
  };

  // ── Scenes List Tab ──
  const renderScenesList = () => {
    // If a scene is selected, show the editor inline (no separate tab needed)
    if (selectedScene) return renderEditor();

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Scenes ({scenes.length})</h3>
          <button onClick={() => { setEditingScene({}); setShowSceneModal(true); }}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition-all">
            + Add Scene
          </button>
        </div>
        {scenes.length === 0 ? (
          <div className={`${cardClass} p-8 text-center`}>
            <p className={`text-lg mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No scenes yet</p>
            <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Create your first scene to get started</p>
          </div>
        ) : (
          <div className="space-y-5">
            {(() => {
              // Group scenes by category, using admin-defined order
              const grouped = {};
              scenes.forEach(scene => {
                const cat = scene.category || 'Uncategorized';
                if (!grouped[cat]) grouped[cat] = [];
                grouped[cat].push(scene);
              });
              const catOrder = Object.keys(grouped).sort((a, b) => {
                if (a === 'Uncategorized') return 1;
                if (b === 'Uncategorized') return -1;
                const idxA = savedCategories.indexOf(a);
                const idxB = savedCategories.indexOf(b);
                if (idxA >= 0 && idxB >= 0) return idxA - idxB;
                if (idxA >= 0) return -1;
                if (idxB >= 0) return 1;
                return a.localeCompare(b);
              });
              return catOrder.map(cat => (
                <div key={cat} className={`rounded-2xl overflow-hidden ${
                  isDarkMode ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-white border border-gray-200 shadow-sm'
                }`}>
                  {/* Category header */}
                  <div className={`px-5 py-3 flex items-center justify-between ${
                    isDarkMode ? 'bg-gray-800/80' : 'bg-gray-50/80'
                  }`}>
                    <div className="flex items-center gap-2.5">
                      <span className="text-lg">📂</span>
                      <h4 className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{cat}</h4>
                    </div>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${
                      isDarkMode ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-50 text-purple-600'
                    }`}>
                      {grouped[cat].length} scene{grouped[cat].length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className={isDarkMode ? 'border-t border-gray-700/50' : 'border-t border-gray-100'} />
                  {/* Scene cards */}
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {grouped[cat].map(scene => (
                        <div key={scene.id} className={`${cardClass} overflow-hidden cursor-pointer hover:ring-2 hover:ring-purple-500 transition-all`}
                          onClick={() => openSceneEditor(scene)}>
                          {scene.image_url ? (
                            <div className="h-40 bg-gray-200 overflow-hidden">
                              <img src={resolveImageUrl(scene.image_url)} alt={scene.name}
                                className="w-full h-full object-cover" />
                            </div>
                          ) : (
                            <div className={`h-40 flex items-center justify-center ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                              <span className="text-4xl">🖼️</span>
                            </div>
                          )}
                          <div className="p-4">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                {/* Reorder arrows */}
                                <div className="flex flex-col -my-1">
                                  <button onClick={(e) => { e.stopPropagation(); moveScene(scene.id, 'up'); }}
                                    disabled={scenes.indexOf(scene) === 0}
                                    className={`p-0.5 rounded text-[10px] leading-none transition-all ${scenes.indexOf(scene) === 0 ? 'opacity-20 cursor-not-allowed' : isDarkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}>
                                    ▲
                                  </button>
                                  <button onClick={(e) => { e.stopPropagation(); moveScene(scene.id, 'down'); }}
                                    disabled={scenes.indexOf(scene) === scenes.length - 1}
                                    className={`p-0.5 rounded text-[10px] leading-none transition-all ${scenes.indexOf(scene) === scenes.length - 1 ? 'opacity-20 cursor-not-allowed' : isDarkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}>
                                    ▼
                                  </button>
                                </div>
                                <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{scene.name}</h4>
                              </div>
                              <div className="flex items-center gap-1">
                                {scene.is_premium && (
                                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-500 rounded-full text-[10px] font-bold">PRO</span>
                                )}
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${scene.is_active
                                  ? 'bg-emerald-500/20 text-emerald-500' : 'bg-red-500/20 text-red-500'}`}>
                                  {scene.is_active ? 'VISIBLE' : 'HIDDEN'}
                                </span>
                                {scene.is_locked && (
                                  <span className="px-2 py-0.5 bg-orange-500/20 text-orange-500 rounded-full text-[10px] font-bold">LOCKED</span>
                                )}
                              </div>
                            </div>
                            {(scene.name_darija_latin || scene.name_ar) && (
                              <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {scene.name_darija_latin}{scene.name_darija_latin && scene.name_ar ? ' · ' : ''}{scene.name_ar}
                              </p>
                            )}
                            <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {scene.object_count || 0} objects marked — Click to open editor
                            </p>
                            <div className="flex gap-2">
                              <button onClick={(e) => { e.stopPropagation(); setEditingScene(scene); setShowSceneModal(true); }}
                                className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
                                Edit Details
                              </button>
                              <button onClick={(e) => {
                                  e.stopPropagation();
                                  const deps = scenes.filter(s => s.unlock_requires?.includes(scene.id));
                                  setResetConfirm({ sceneId: scene.id, sceneName: scene.name, dependentScenes: deps });
                                }}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${isDarkMode ? 'bg-gray-700 hover:bg-orange-900/30 text-gray-400 hover:text-orange-400' : 'bg-gray-100 hover:bg-orange-50 text-gray-500 hover:text-orange-500'}`}>
                                Reset Progress
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); deleteScene(scene.id); }}
                                className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-500 rounded-lg text-xs font-semibold transition-all">
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ));
            })()}
          </div>
        )}
      </div>
    );
  };

  // ── Scene Editor Tab ──
  const renderEditor = () => {
    if (!selectedScene) return (
      <div className={`${cardClass} p-8 text-center`}>
        <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Select a scene from the Scenes tab to edit</p>
      </div>
    );

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => { setSelectedScene(null); setSceneObjects([]); }}
              className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Editing: {selectedScene.name}
            </h3>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => { setEditingScene(selectedScene); setShowSceneModal(true); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
              Edit Scene Details
            </button>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Click on image to add markers ({sceneObjects.length} objects)
            </p>
          </div>
        </div>

        {/* Image Canvas */}
        <div className={`${cardClass} p-4`}>
          {selectedScene.image_url ? (
            <div className="relative inline-block w-full cursor-crosshair" onClick={handleImageClick}>
              <img ref={imageRef} src={resolveImageUrl(selectedScene.image_url)} alt={selectedScene.name}
                className="w-full rounded-lg" draggable={false} />
              {/* Dot markers */}
              {sceneObjects.map((obj, i) => (
                <div key={obj.id}
                  className="absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer group"
                  style={{ left: `${obj.position_x}%`, top: `${obj.position_y}%` }}
                  onClick={(e) => { e.stopPropagation(); setEditingObject(obj); setShowObjectModal(true); }}>
                  {/* Pulsing dot */}
                  <div className="absolute inset-0 w-5 h-5 rounded-full bg-emerald-400/40 animate-ping" />
                  <div className="relative w-5 h-5 rounded-full bg-emerald-500 border-2 border-white shadow-lg flex items-center justify-center">
                    <span className="text-[8px] font-bold text-white">{i + 1}</span>
                  </div>
                  {/* Always-visible label */}
                  {obj.labels?.en && (
                    <div className="absolute left-6 top-0 z-10 whitespace-nowrap pointer-events-none">
                      <div className="bg-gray-900/90 text-white text-[10px] font-semibold px-2 py-0.5 rounded shadow-lg">
                        {obj.labels.en}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className={`h-64 flex items-center justify-center rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>No image uploaded — edit scene to add one</p>
            </div>
          )}
        </div>

        {/* Objects List */}
        <div className={`${cardClass} p-4`}>
          <h4 className={`font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Objects ({sceneObjects.length})</h4>
          {sceneObjects.length === 0 ? (
            <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Click on the image above to place markers</p>
          ) : (
            <div className="space-y-2">
              {sceneObjects.map((obj, i) => {
                const filledLangs = configuredLanguages.filter(l => (obj.labels?.[l.code] || '').trim());
                const totalLangs = configuredLanguages.length || 3;
                return (
                  <div key={obj.id} className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs font-bold shrink-0">{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{obj.labels?.en || 'No English label'}</p>
                        {obj.labels?.darija_latin && (
                          <p className={`text-xs ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>{obj.labels.darija_latin}{obj.labels?.darija_script ? ` · ${obj.labels.darija_script}` : ''}</p>
                        )}
                        <div className="flex flex-wrap gap-1 mt-1">
                          {configuredLanguages.map(l => {
                            const filled = (obj.labels?.[l.code] || '').trim();
                            return (
                              <span key={l.code} className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                                filled
                                  ? 'bg-emerald-500/20 text-emerald-500'
                                  : isDarkMode ? 'bg-gray-600/50 text-gray-500' : 'bg-gray-200/50 text-gray-400'
                              }`}>
                                {l.name?.slice(0, 3).toUpperCase()}
                              </span>
                            );
                          })}
                          <span className={`text-[9px] px-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {filledLangs.length}/{totalLangs}
                          </span>
                          {(() => {
                            const audioCount = configuredLanguages.filter(l => (obj.audio_urls?.[l.code] || '').trim()).length;
                            return audioCount > 0 ? (
                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-500 ml-1">
                                {audioCount} audio
                              </span>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0 ml-2">
                      <button onClick={() => { setEditingObject(obj); setShowObjectModal(true); }}
                        className={`px-3 py-1.5 rounded text-xs font-semibold ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>
                        Edit
                      </button>
                      <button onClick={() => deleteObject(obj.id)}
                        className="px-3 py-1.5 rounded text-xs font-semibold bg-red-500/20 hover:bg-red-500/30 text-red-500">
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Categories from settings (stored in DB)
  const savedCategories = settings?.categories || [];
  const usedCategories = [...new Set(scenes.map(s => s.category).filter(Boolean))];
  const allCategories = [...new Set([...savedCategories, ...usedCategories])].sort();

  // ── Scene Modal ──
  const SceneModal = () => {
    const [form, setForm] = useState({
      name: editingScene?.name || '',
      name_darija_latin: editingScene?.name_darija_latin || '',
      name_ar: editingScene?.name_ar || '',
      description: editingScene?.description || '',
      category: editingScene?.category || '',
      image_url: editingScene?.image_url || '',
      is_active: editingScene?.is_active ?? true,
      is_premium: editingScene?.is_premium ?? false,
      is_locked: editingScene?.is_locked ?? false,
      unlock_requires: editingScene?.unlock_requires || [],
      order: editingScene?.order ?? scenes.length,
      quiz_config: editingScene?.quiz_config || {}
    });
    const [customCategory, setCustomCategory] = useState(
      editingScene?.category && !savedCategories.includes(editingScene.category) ? editingScene.category : ''
    );
    const [uploading, setUploading] = useState(false);
    const [modalObjects, setModalObjects] = useState([]);
    const [imageMode, setImageMode] = useState(
      editingScene?.image_url && (editingScene.image_url.startsWith('http://') || editingScene.image_url.startsWith('https://'))
        ? 'url' : 'upload'
    );

    // Load scene objects when editing existing scene (for quiz settings)
    useEffect(() => {
      if (editingScene?.id) {
        fetch(`${API}/admin/scene-explorer/scenes/${editingScene.id}/objects`, { headers })
          .then(r => r.ok ? r.json() : [])
          .then(data => setModalObjects(Array.isArray(data) ? data : []))
          .catch(() => setModalObjects([]));
      }
    }, [editingScene?.id]);

    const handleUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      const url = await uploadImage(file);
      if (url) setForm(prev => ({ ...prev, image_url: url }));
      setUploading(false);
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setShowSceneModal(false); setEditingScene(null); }}>
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
          <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            {editingScene?.id ? 'Edit Scene' : 'New Scene'}
          </h3>
          <div className="space-y-4">
            {/* Scene Name (English) */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Scene Name (English) *</label>
              <input className={inputClass} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Kitchen" />
            </div>
            {/* Darija Latin */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Darija Latin Name</label>
              <input className={inputClass} value={form.name_darija_latin} onChange={e => setForm({ ...form, name_darija_latin: e.target.value })} placeholder="e.g. Kouzina" />
            </div>
            {/* Arabic Name */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Arabic Script Name</label>
              <input className={inputClass} value={form.name_ar} onChange={e => setForm({ ...form, name_ar: e.target.value })} placeholder="e.g. كوزينة" dir="rtl" />
            </div>
            {/* Description */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Description</label>
              <textarea className={inputClass} rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="e.g. Learn kitchen vocabulary by exploring objects" />
            </div>
            {/* Category */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Category *</label>
              <div className="flex flex-wrap gap-2">
                {allCategories.map(cat => (
                  <button key={cat} type="button" onClick={() => { setForm({ ...form, category: cat }); setCustomCategory(''); }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      form.category === cat ? 'bg-purple-600 text-white' : isDarkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}>
                    {cat}
                  </button>
                ))}
              </div>
              <input className={`${inputClass} mt-2`} value={customCategory || (!allCategories.includes(form.category) ? form.category : '')}
                onChange={e => { setCustomCategory(e.target.value); setForm({ ...form, category: e.target.value }); }}
                placeholder="Or type a custom category..." />
            </div>
            {/* Image — Upload or URL */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Scene Image</label>
              <div className="flex gap-2 mb-2">
                <button onClick={() => setImageMode('upload')}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${imageMode === 'upload' ? 'bg-purple-600 text-white' : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                  Upload File
                </button>
                <button onClick={() => setImageMode('url')}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${imageMode === 'url' ? 'bg-purple-600 text-white' : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                  Dropbox / URL
                </button>
              </div>
              {imageMode === 'upload' ? (
                <>
                  <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleUpload} className={inputClass} />
                  {uploading && <p className="text-xs text-purple-500 mt-1">Uploading...</p>}
                </>
              ) : (
                <input className={inputClass} value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })}
                  placeholder="https://www.dropbox.com/... or any image URL" />
              )}
              {form.image_url && (
                <div className="mt-2">
                  <img src={resolveImageUrl(form.image_url)} alt="Preview" className="rounded-lg max-h-40 object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
              )}
            </div>
            {/* Toggles */}
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 rounded" />
                <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Visible</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_premium} onChange={e => setForm({ ...form, is_premium: e.target.checked })} className="w-4 h-4 rounded" />
                <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Pro Only</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_locked} onChange={e => setForm({ ...form, is_locked: e.target.checked, unlock_requires: e.target.checked ? form.unlock_requires : [] })} className="w-4 h-4 rounded" />
                <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Locked</span>
              </label>
            </div>
            {/* Unlock requires — only when locked */}
            {form.is_locked && (
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Unlock requires (user must complete these with 100%)
                </label>
                <div className={`space-y-1 max-h-40 overflow-y-auto p-3 rounded-lg border ${isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                  {scenes.filter(s => s.id !== editingScene?.id).length === 0 ? (
                    <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No other scenes to select</p>
                  ) : (
                    scenes.filter(s => s.id !== editingScene?.id).map(s => (
                      <label key={s.id} className="flex items-center gap-2 cursor-pointer py-0.5">
                        <input type="checkbox"
                          checked={form.unlock_requires.includes(s.id)}
                          onChange={e => {
                            if (e.target.checked) {
                              setForm({ ...form, unlock_requires: [...form.unlock_requires, s.id] });
                            } else {
                              setForm({ ...form, unlock_requires: form.unlock_requires.filter(id => id !== s.id) });
                            }
                          }}
                          className="w-4 h-4 rounded" />
                        <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {s.name} {s.category ? `(${s.category})` : ''}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}
            {/* Order */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Order</label>
              <input type="number" className={inputClass} value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })} />
            </div>
            {/* Quiz Settings (per-scene override) */}
            <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-gray-700/30 border-gray-600' : 'bg-purple-50/50 border-purple-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <h4 className={`text-sm font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Quiz Settings</h4>
                {Object.keys(form.quiz_config).length > 0 && (
                  <button type="button" onClick={() => setForm({ ...form, quiz_config: {} })}
                    className="text-xs text-purple-500 hover:text-purple-400 underline">Reset to defaults</button>
                )}
              </div>
              <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Inherit = auto (uses all objects, global defaults). Enabled = manual (pick objects, custom question).
              </p>
              {/* Match the Label */}
              <div className={`p-3 rounded-lg mb-3 ${isDarkMode ? 'bg-gray-700/50' : 'bg-white'}`}>
                <p className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {settings?.game_settings?.match_game?.name || 'Match the Label'}
                </p>
                <select className={`${inputClass} text-xs mb-2`}
                  value={form.quiz_config?.match_game?.enabled === true ? 'on' : form.quiz_config?.match_game?.enabled === false ? 'off' : 'inherit'}
                  onChange={e => {
                    const val = e.target.value;
                    const mg = { ...(form.quiz_config?.match_game || {}) };
                    if (val === 'inherit') { delete mg.enabled; delete mg.included_objects; }
                    else mg.enabled = val === 'on';
                    setForm({ ...form, quiz_config: { ...form.quiz_config, match_game: mg } });
                  }}>
                  <option value="inherit">Inherit ({settings?.game_settings?.match_game?.enabled !== false ? 'On' : 'Off'}) — auto, all objects</option>
                  <option value="on">Enabled — manual, pick objects</option>
                  <option value="off">Disabled</option>
                </select>
                {/* Show config only when Enabled (manual) */}
                {form.quiz_config?.match_game?.enabled === true && (
                  <>
                    <input className={`${inputClass} text-xs mb-2`}
                      value={form.quiz_config?.match_game?.question_text || ''}
                      onChange={e => {
                        const mg = { ...(form.quiz_config?.match_game || {}), question_text: e.target.value };
                        if (!e.target.value) delete mg.question_text;
                        setForm({ ...form, quiz_config: { ...form.quiz_config, match_game: mg } });
                      }}
                      placeholder={settings?.game_settings?.match_game?.question_text || 'What is this in Darija?'} />
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Options:</span>
                      <input type="number" min={2} max={6} className={`${inputClass} text-xs w-16`}
                        value={form.quiz_config?.match_game?.num_options || ''}
                        onChange={e => {
                          const mg = { ...(form.quiz_config?.match_game || {}) };
                          const v = parseInt(e.target.value);
                          if (v >= 2 && v <= 6) mg.num_options = v;
                          else delete mg.num_options;
                          setForm({ ...form, quiz_config: { ...form.quiz_config, match_game: mg } });
                        }}
                        placeholder={String(settings?.game_settings?.match_game?.num_options || 4)} />
                    </div>
                    {/* Object checklist */}
                    {modalObjects.length > 0 ? (
                      <div className={`p-2 rounded-lg border max-h-40 overflow-y-auto ${isDarkMode ? 'bg-gray-800/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Objects ({(form.quiz_config?.match_game?.included_objects || []).length || modalObjects.length}/{modalObjects.length})
                          </span>
                          <button type="button" onClick={() => {
                            const mg = { ...(form.quiz_config?.match_game || {}) };
                            delete mg.included_objects;
                            setForm({ ...form, quiz_config: { ...form.quiz_config, match_game: mg } });
                          }} className="text-xs text-purple-500 hover:text-purple-400">Select all</button>
                        </div>
                        {modalObjects.map(obj => {
                          const included = form.quiz_config?.match_game?.included_objects;
                          const isChecked = !included || included.length === 0 || included.includes(obj.id);
                          return (
                            <label key={obj.id} className="flex items-center gap-2 py-0.5 cursor-pointer">
                              <input type="checkbox" checked={isChecked} className="w-3.5 h-3.5 rounded"
                                onChange={e => {
                                  const mg = { ...(form.quiz_config?.match_game || {}) };
                                  let list = mg.included_objects || modalObjects.map(o => o.id);
                                  if (e.target.checked) {
                                    list = [...new Set([...list, obj.id])];
                                  } else {
                                    list = list.filter(id => id !== obj.id);
                                  }
                                  if (list.length === modalObjects.length) delete mg.included_objects;
                                  else mg.included_objects = list;
                                  setForm({ ...form, quiz_config: { ...form.quiz_config, match_game: mg } });
                                }} />
                              <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                {obj.labels?.en || obj.id} {obj.labels?.darija_latin ? `(${obj.labels.darija_latin})` : ''}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    ) : editingScene?.id ? (
                      <p className={`text-xs italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No objects in this scene yet</p>
                    ) : null}
                  </>
                )}
              </div>
              {/* Is This Correct? */}
              <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-white'}`}>
                <p className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {settings?.game_settings?.correct_game?.name || 'Is This Correct?'}
                </p>
                <select className={`${inputClass} text-xs mb-2`}
                  value={form.quiz_config?.correct_game?.enabled === true ? 'on' : form.quiz_config?.correct_game?.enabled === false ? 'off' : 'inherit'}
                  onChange={e => {
                    const val = e.target.value;
                    const cg = { ...(form.quiz_config?.correct_game || {}) };
                    if (val === 'inherit') { delete cg.enabled; delete cg.included_objects; }
                    else cg.enabled = val === 'on';
                    setForm({ ...form, quiz_config: { ...form.quiz_config, correct_game: cg } });
                  }}>
                  <option value="inherit">Inherit ({settings?.game_settings?.correct_game?.enabled !== false ? 'On' : 'Off'}) — auto, all objects</option>
                  <option value="on">Enabled — manual, pick objects</option>
                  <option value="off">Disabled</option>
                </select>
                {/* Show config only when Enabled (manual) */}
                {form.quiz_config?.correct_game?.enabled === true && (
                  <>
                    <input className={`${inputClass} text-xs mb-2`}
                      value={form.quiz_config?.correct_game?.question_text || ''}
                      onChange={e => {
                        const cg = { ...(form.quiz_config?.correct_game || {}), question_text: e.target.value };
                        if (!e.target.value) delete cg.question_text;
                        setForm({ ...form, quiz_config: { ...form.quiz_config, correct_game: cg } });
                      }}
                      placeholder={settings?.game_settings?.correct_game?.question_text || 'Is this the correct Darija word?'} />
                    {/* Object checklist */}
                    {modalObjects.length > 0 ? (
                      <div className={`p-2 rounded-lg border max-h-40 overflow-y-auto ${isDarkMode ? 'bg-gray-800/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Objects ({(form.quiz_config?.correct_game?.included_objects || []).length || modalObjects.length}/{modalObjects.length})
                          </span>
                          <button type="button" onClick={() => {
                            const cg = { ...(form.quiz_config?.correct_game || {}) };
                            delete cg.included_objects;
                            setForm({ ...form, quiz_config: { ...form.quiz_config, correct_game: cg } });
                          }} className="text-xs text-purple-500 hover:text-purple-400">Select all</button>
                        </div>
                        {modalObjects.map(obj => {
                          const included = form.quiz_config?.correct_game?.included_objects;
                          const isChecked = !included || included.length === 0 || included.includes(obj.id);
                          return (
                            <label key={obj.id} className="flex items-center gap-2 py-0.5 cursor-pointer">
                              <input type="checkbox" checked={isChecked} className="w-3.5 h-3.5 rounded"
                                onChange={e => {
                                  const cg = { ...(form.quiz_config?.correct_game || {}) };
                                  let list = cg.included_objects || modalObjects.map(o => o.id);
                                  if (e.target.checked) {
                                    list = [...new Set([...list, obj.id])];
                                  } else {
                                    list = list.filter(id => id !== obj.id);
                                  }
                                  if (list.length === modalObjects.length) delete cg.included_objects;
                                  else cg.included_objects = list;
                                  setForm({ ...form, quiz_config: { ...form.quiz_config, correct_game: cg } });
                                }} />
                              <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                {obj.labels?.en || obj.id} {obj.labels?.darija_latin ? `(${obj.labels.darija_latin})` : ''}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    ) : editingScene?.id ? (
                      <p className={`text-xs italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No objects in this scene yet</p>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={() => saveScene(form)}
              className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold text-sm transition-all">
              {editingScene?.id ? 'Update' : 'Create'}
            </button>
            <button onClick={() => { setShowSceneModal(false); setEditingScene(null); }}
              className={`px-4 py-2 rounded-lg font-semibold text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Object Modal ──
  const ObjectModal = () => {
    // Always guarantee core languages exist, then add configured + any extra from object
    const CORE_CODES = ['en', 'darija_latin', 'darija_script'];
    const coreDefaults = [
      { code: 'en', name: 'English', dir: 'ltr', is_core: true },
      { code: 'darija_latin', name: 'Darija Latin', dir: 'ltr', is_core: true },
      { code: 'darija_script', name: 'Arabic Script', dir: 'rtl', is_core: true },
    ];
    // Start with configured languages
    const effectiveLangs = [...configuredLanguages];
    // Ensure core languages are always present (even if missing from settings)
    for (const core of coreDefaults) {
      if (!effectiveLangs.some(l => l.code === core.code)) {
        effectiveLangs.unshift(core);
      }
    }
    // Also include any extra keys from the object that aren't in the list
    if (editingObject?.labels) {
      for (const key of Object.keys(editingObject.labels)) {
        if (!effectiveLangs.some(l => l.code === key) && (editingObject.labels[key] || '').trim()) {
          effectiveLangs.push({ code: key, name: key, dir: 'ltr', is_core: false });
        }
      }
    }
    const coreLangs = effectiveLangs.filter(l => l.is_core || CORE_CODES.includes(l.code));
    const otherLangs = effectiveLangs.filter(l => !l.is_core && !CORE_CODES.includes(l.code));
    const initialLabels = {};
    effectiveLangs.forEach(l => { initialLabels[l.code] = editingObject?.labels?.[l.code] || ''; });
    const initialAudioUrls = {};
    effectiveLangs.forEach(l => { initialAudioUrls[l.code] = editingObject?.audio_urls?.[l.code] || ''; });

    const [form, setForm] = useState({
      position_x: editingObject?.position_x ?? 50,
      position_y: editingObject?.position_y ?? 50,
      labels: initialLabels,
      audio_urls: initialAudioUrls
    });
    const [showOtherLangs, setShowOtherLangs] = useState(
      otherLangs.some(l => !!editingObject?.labels?.[l.code])
    );

    const [translating, setTranslating] = useState(false);

    const updateLabel = (lang, value) => {
      setForm(prev => ({ ...prev, labels: { ...prev.labels, [lang]: value } }));
    };
    const updateAudioUrl = (lang, value) => {
      setForm(prev => ({ ...prev, audio_urls: { ...prev.audio_urls, [lang]: value } }));
    };
    const [showAudioUrls, setShowAudioUrls] = useState(
      effectiveLangs.some(l => (editingObject?.audio_urls?.[l.code] || '').trim())
    );

    const handleAutoTranslate = async () => {
      const englishText = (form.labels['en'] || '').trim();
      if (!englishText) { showNotification('Enter an English label first', 'error'); return; }

      // Only translate non-core languages that have empty labels (admin enters EN, Darija, Arabic manually)
      const SKIP_CODES = ['en', 'darija_latin', 'darija_script'];
      const targets = effectiveLangs
        .filter(l => !SKIP_CODES.includes(l.code) && !(form.labels[l.code] || '').trim())
        .map(l => ({ code: l.code, name: l.name }));

      if (targets.length === 0) { showNotification('All languages already have translations', 'info'); return; }

      setTranslating(true);
      try {
        const res = await fetch(`${API}/admin/scene-explorer/auto-translate`, {
          method: 'POST', headers,
          body: JSON.stringify({ english_text: englishText, target_languages: targets })
        });
        if (res.ok) {
          const data = await res.json();
          if (Object.keys(data.translations).length > 0) {
            setForm(prev => ({ ...prev, labels: { ...prev.labels, ...data.translations } }));
            showNotification(`Translated to ${Object.keys(data.translations).length} languages`, 'success');
            // Auto-expand other languages so admin sees results
            if (otherLangs.some(l => data.translations[l.code])) setShowOtherLangs(true);
          }
          if (data.errors?.length > 0) {
            showNotification(`Some failed: ${data.errors.join(', ')}`, 'error');
          }
        } else {
          const err = await res.json().catch(() => ({}));
          showNotification(err.detail || 'Translation failed', 'error');
        }
      } catch (e) {
        showNotification('Network error during translation', 'error');
      } finally {
        setTranslating(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setShowObjectModal(false); setEditingObject(null); }}>
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
          <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            {editingObject?.id ? `Edit: ${editingObject?.labels?.en || 'Object'}` : 'New Object Marker'}
          </h3>
          <p className={`text-xs mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Position: ({form.position_x}%, {form.position_y}%)
          </p>

          {/* ── Core Labels (always visible) ── */}
          <div className="space-y-3">
            {coreLangs.map(lang => (
              <div key={lang.code}>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {lang.name} {lang.code === 'en' ? '*' : ''}
                </label>
                <input className={inputClass} value={form.labels[lang.code] || ''}
                  onChange={e => updateLabel(lang.code, e.target.value)}
                  dir={lang.dir === 'rtl' ? 'rtl' : undefined} />
              </div>
            ))}
          </div>

          {/* ── Auto-Translate Button ── */}
          {effectiveLangs.length > 1 && (
            <div className="mt-4 mb-1">
              <button onClick={handleAutoTranslate}
                disabled={translating || !(form.labels['en'] || '').trim()}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  translating || !(form.labels['en'] || '').trim()
                    ? isDarkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}>
                {translating ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Translating...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                    </svg>
                    Auto-Translate from English
                  </>
                )}
              </button>
              {!(form.labels['en'] || '').trim() && (
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Enter an English label first to enable auto-translate
                </p>
              )}
            </div>
          )}

          {/* ── Other Languages (collapsible) ── */}
          {otherLangs.length > 0 && (
            <div className="mt-5">
              <button onClick={() => setShowOtherLangs(!showOtherLangs)}
                className={`flex items-center gap-2 text-sm font-semibold ${isDarkMode ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'} transition-colors`}>
                <svg className={`w-4 h-4 transition-transform ${showOtherLangs ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                Translate to other languages ({otherLangs.length})
              </button>
              {showOtherLangs && (
                <div className="space-y-3 mt-3 pl-2 border-l-2 border-purple-500/30">
                  {otherLangs.map(lang => {
                    const isoMap = { fr: 'fr', es: 'es', de: 'de', it: 'it', pt: 'pt', ar: 'ar', ja: 'ja', zh: 'zh-CN', ko: 'ko', nl: 'nl', ru: 'ru', hi: 'hi', tr: 'tr', sv: 'sv', ar_standard: 'ar' };
                    const isoCode = isoMap[lang.code] || lang.code;
                    const englishText = (form.labels['en'] || '').trim();
                    const hasValue = (form.labels[lang.code] || '').trim();
                    return (
                    <div key={lang.code}>
                      <div className="flex items-center justify-between mb-1">
                        <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{lang.name}</label>
                        {hasValue && englishText && (
                          <a href={`https://translate.google.com/?sl=en&tl=${isoCode}&text=${encodeURIComponent(englishText)}&op=translate`}
                            target="_blank" rel="noopener noreferrer"
                            className={`text-[10px] font-semibold ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-500 hover:text-blue-600'}`}>
                            Verify
                          </a>
                        )}
                      </div>
                      <input className={inputClass} value={form.labels[lang.code] || ''}
                        onChange={e => updateLabel(lang.code, e.target.value)}
                        dir={lang.dir === 'rtl' ? 'rtl' : undefined} />
                    </div>
                  );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Audio URLs (collapsible) ── */}
          <div className="mt-5">
            <button onClick={() => setShowAudioUrls(!showAudioUrls)}
              className={`flex items-center gap-2 text-sm font-semibold ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'} transition-colors`}>
              <svg className={`w-4 h-4 transition-transform ${showAudioUrls ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Audio URLs ({effectiveLangs.filter(l => (form.audio_urls?.[l.code] || '').trim()).length}/{effectiveLangs.length})
            </button>
            {showAudioUrls && (
              <div className="space-y-3 mt-3 pl-2 border-l-2 border-blue-500/30">
                <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Paste Dropbox MP3 URLs for each language (optional)
                </p>
                {effectiveLangs.map(lang => (
                  <div key={lang.code}>
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {lang.name} audio
                    </label>
                    <input className={inputClass} value={form.audio_urls?.[lang.code] || ''}
                      onChange={e => updateAudioUrl(lang.code, e.target.value)}
                      placeholder="https://www.dropbox.com/s/.../word.mp3?dl=0" />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-6">
            <button onClick={() => saveObject(form)}
              className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold text-sm transition-all">
              {editingObject?.id ? 'Update' : 'Add Object'}
            </button>
            <button onClick={() => { setShowObjectModal(false); setEditingObject(null); }}
              className={`px-4 py-2 rounded-lg font-semibold text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Categories Tab ──
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState('');

  const addCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    if (savedCategories.includes(name)) { showNotification('Category already exists', 'error'); return; }
    const updated = [...savedCategories, name];
    await saveSettings({ categories: updated });
    setNewCategoryName('');
  };

  const renameCategory = async (oldName) => {
    const newName = editCategoryName.trim();
    if (!newName || newName === oldName) { setEditingCategory(null); return; }
    if (savedCategories.includes(newName)) { showNotification('Category name already exists', 'error'); return; }
    const updated = savedCategories.map(c => c === oldName ? newName : c);
    await saveSettings({ categories: updated });
    // Update scenes that use the old category
    for (const scene of scenes.filter(s => s.category === oldName)) {
      try {
        await fetch(`${API}/admin/scene-explorer/scenes/${scene.id}`, {
          method: 'PUT', headers, body: JSON.stringify({ category: newName })
        });
      } catch (e) { /* best effort */ }
    }
    loadScenes();
    setEditingCategory(null);
    showNotification(`Renamed "${oldName}" to "${newName}"`, 'success');
  };

  const moveCategory = async (catName, direction) => {
    const idx = savedCategories.indexOf(catName);
    if (idx < 0) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= savedCategories.length) return;
    const updated = [...savedCategories];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    await saveSettings({ categories: updated });
  };

  const deleteCategory = async (catName, skipConfirm = false) => {
    const scenesInCat = scenes.filter(s => s.category === catName);
    if (!skipConfirm) {
      const msg = scenesInCat.length > 0
        ? `Delete "${catName}"? ${scenesInCat.length} scene(s) will be moved to "Other".`
        : `Delete "${catName}"?`;
      setConfirmModal({
        title: 'Delete Category',
        message: msg,
        confirmText: 'Delete',
        confirmColor: 'red',
        onConfirm: () => { setConfirmModal(null); deleteCategory(catName, true); }
      });
      return;
    }
    const updated = savedCategories.filter(c => c !== catName);
    await saveSettings({ categories: updated });
    // Clear category from scenes that use it
    for (const scene of scenesInCat) {
      try {
        await fetch(`${API}/admin/scene-explorer/scenes/${scene.id}`, {
          method: 'PUT', headers, body: JSON.stringify({ category: '' })
        });
      } catch (e) { /* best effort */ }
    }
    loadScenes();
    showNotification(`Category "${catName}" deleted`, 'success');
  };

  const renderCategories = () => (
    <div className="space-y-4">
      <div className={`${cardClass} p-6`}>
        <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Categories ({savedCategories.length})</h3>
        <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Manage scene categories. Scenes are grouped by category in the user view.
        </p>

        {/* Add new category */}
        <div className="flex gap-2 mb-4">
          <input className={`${inputClass} flex-1`} value={newCategoryName}
            onChange={e => setNewCategoryName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addCategory()}
            placeholder="New category name..." />
          <button onClick={addCategory}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition-all whitespace-nowrap">
            + Add
          </button>
        </div>

        {/* Category list */}
        {savedCategories.length === 0 ? (
          <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No categories yet. Add one above.</p>
        ) : (
          <div className="space-y-2">
            {savedCategories.map((cat, i) => {
              const count = scenes.filter(s => s.category === cat).length;
              return (
                <div key={cat} className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                  {editingCategory === cat ? (
                    <div className="flex items-center gap-2 flex-1 mr-2">
                      <input className={`${inputClass} flex-1`} value={editCategoryName}
                        onChange={e => setEditCategoryName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') renameCategory(cat); if (e.key === 'Escape') setEditingCategory(null); }}
                        autoFocus />
                      <button onClick={() => renameCategory(cat)}
                        className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold">Save</button>
                      <button onClick={() => setEditingCategory(null)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-gray-200 text-gray-700'}`}>Cancel</button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        {/* Reorder arrows */}
                        <div className="flex flex-col">
                          <button onClick={() => moveCategory(cat, 'up')} disabled={i === 0}
                            className={`text-xs p-0.5 ${i === 0 ? 'opacity-20' : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>▲</button>
                          <button onClick={() => moveCategory(cat, 'down')} disabled={i === savedCategories.length - 1}
                            className={`text-xs p-0.5 ${i === savedCategories.length - 1 ? 'opacity-20' : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>▼</button>
                        </div>
                        <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{cat}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                          {count} scene{count !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => { setEditingCategory(cat); setEditCategoryName(cat); }}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>
                          Rename
                        </button>
                        <button onClick={() => deleteCategory(cat)}
                          className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-500 rounded-lg text-xs font-semibold">
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  // ── Languages Tab ──
  const configuredLanguages = (settings?.languages || []).sort((a, b) => (a.order || 0) - (b.order || 0));
  const [newLangCode, setNewLangCode] = useState('');
  const [newLangName, setNewLangName] = useState('');
  const [newLangDir, setNewLangDir] = useState('ltr');
  const [editingLang, setEditingLang] = useState(null);
  const [editLangName, setEditLangName] = useState('');

  const addLanguage = async () => {
    const code = newLangCode.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
    const name = newLangName.trim();
    if (!code || !name) { showNotification('Code and name required', 'error'); return; }
    if (configuredLanguages.some(l => l.code === code)) { showNotification('Language code already exists', 'error'); return; }
    const updated = [...configuredLanguages, { code, name, dir: newLangDir, is_core: false, order: configuredLanguages.length }];
    await saveSettings({ languages: updated });
    setNewLangCode(''); setNewLangName(''); setNewLangDir('ltr');
  };

  const renameLang = async (code) => {
    const name = editLangName.trim();
    if (!name) { setEditingLang(null); return; }
    const updated = configuredLanguages.map(l => l.code === code ? { ...l, name } : l);
    await saveSettings({ languages: updated });
    setEditingLang(null);
    showNotification('Language renamed', 'success');
  };

  const deleteLang = async (code, skipConfirm = false) => {
    const lang = configuredLanguages.find(l => l.code === code);
    if (lang?.is_core) return;
    if (!skipConfirm) {
      setConfirmModal({
        title: 'Remove Language',
        message: `Remove "${lang.name}" (${code})? Existing translations will be kept but hidden.`,
        confirmText: 'Remove',
        confirmColor: 'red',
        onConfirm: () => { setConfirmModal(null); deleteLang(code, true); }
      });
      return;
    }
    const updated = configuredLanguages.filter(l => l.code !== code).map((l, i) => ({ ...l, order: i }));
    await saveSettings({ languages: updated });
    showNotification(`"${lang.name}" removed`, 'success');
  };

  const toggleLangDir = async (code) => {
    const updated = configuredLanguages.map(l => l.code === code ? { ...l, dir: l.dir === 'rtl' ? 'ltr' : 'rtl' } : l);
    await saveSettings({ languages: updated });
  };

  const reorderLang = async (code, dir) => {
    const idx = configuredLanguages.findIndex(l => l.code === code);
    if (idx < 0) return;
    const newIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= configuredLanguages.length) return;
    const updated = [...configuredLanguages];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    await saveSettings({ languages: updated.map((l, i) => ({ ...l, order: i })) });
  };

  const renderLanguages = () => (
    <div className="space-y-4">
      <div className={`${cardClass} p-6`}>
        <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Languages ({configuredLanguages.length})</h3>
        <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Configure languages for object labels. Core languages (English, Darija, Arabic Script) cannot be removed.
        </p>

        {/* Add new language */}
        <div className={`p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
          <p className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Add Language</p>
          <div className="flex gap-2 flex-wrap">
            <input className={`${inputClass} w-28`} value={newLangCode}
              onChange={e => setNewLangCode(e.target.value)}
              placeholder="Code (e.g. de)" />
            <input className={`${inputClass} flex-1`} value={newLangName}
              onChange={e => setNewLangName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addLanguage()}
              placeholder="Display name (e.g. German)" />
            <button onClick={() => setNewLangDir(d => d === 'ltr' ? 'rtl' : 'ltr')}
              className={`px-3 py-2 rounded-lg text-xs font-bold ${newLangDir === 'rtl' ? 'bg-amber-500/20 text-amber-500' : isDarkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-600'}`}>
              {newLangDir.toUpperCase()}
            </button>
            <button onClick={addLanguage}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold">
              + Add
            </button>
          </div>
        </div>

        {/* Language list */}
        <div className="space-y-2">
          {configuredLanguages.map((lang, i) => (
            <div key={lang.code} className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
              {editingLang === lang.code ? (
                <div className="flex items-center gap-2 flex-1 mr-2">
                  <input className={`${inputClass} flex-1`} value={editLangName}
                    onChange={e => setEditLangName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') renameLang(lang.code); if (e.key === 'Escape') setEditingLang(null); }}
                    autoFocus />
                  <button onClick={() => renameLang(lang.code)}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold">Save</button>
                  <button onClick={() => setEditingLang(null)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-gray-200 text-gray-700'}`}>Cancel</button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    {/* Reorder arrows */}
                    <div className="flex flex-col">
                      <button onClick={() => reorderLang(lang.code, 'up')} disabled={i === 0}
                        className={`text-xs p-0.5 ${i === 0 ? 'opacity-20' : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>▲</button>
                      <button onClick={() => reorderLang(lang.code, 'down')} disabled={i === configuredLanguages.length - 1}
                        className={`text-xs p-0.5 ${i === configuredLanguages.length - 1 ? 'opacity-20' : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'}`}>▼</button>
                    </div>
                    <div>
                      <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        {lang.name} <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>({lang.code})</span>
                      </p>
                    </div>
                    <button onClick={() => toggleLangDir(lang.code)} title="Click to toggle direction"
                      className={`text-[10px] font-bold px-1.5 py-0.5 rounded cursor-pointer hover:opacity-80 transition-opacity ${lang.dir === 'rtl' ? 'bg-amber-500/20 text-amber-500' : isDarkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                      {lang.dir?.toUpperCase() || 'LTR'}
                    </button>
                    {lang.is_core && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-500">CORE</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setEditingLang(lang.code); setEditLangName(lang.name); }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>
                      Rename
                    </button>
                    {!lang.is_core && (
                      <button onClick={() => deleteLang(lang.code)}
                        className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-500 rounded-lg text-xs font-semibold">
                        Delete
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ── How to Play Tab ──
  const [howToPlaySteps, setHowToPlaySteps] = useState(null);

  useEffect(() => {
    if (settings?.how_to_play) setHowToPlaySteps(settings.how_to_play);
    else if (settings && !settings.how_to_play) {
      setHowToPlaySteps([
        { title: 'Pick a scene', description: 'Choose a scene like Kitchen, Bedroom, or a nature scene like River' },
        { title: 'Explore the image', description: 'See labeled dots on the image — switch between English, Darija, Arabic, French & more' },
        { title: 'Play the games', description: '"Match the Label" — pick the right word. "Is This Correct?" — decide if the label is right or wrong' }
      ]);
    }
  }, [settings]);

  const saveHowToPlay = async () => {
    if (!howToPlaySteps) return;
    await saveSettings({ how_to_play: howToPlaySteps });
  };

  const updateStep = (idx, field, value) => {
    setHowToPlaySteps(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const addStep = () => {
    setHowToPlaySteps(prev => [...(prev || []), { title: '', description: '' }]);
  };

  const removeStep = (idx) => {
    setHowToPlaySteps(prev => prev.filter((_, i) => i !== idx));
  };

  // ── Games Tab ──
  const gs = settings?.game_settings || {};
  const updateGameSettings = (path, value) => {
    // Deep merge: path like "match_game.name" → nested update
    const keys = path.split('.');
    const updated = JSON.parse(JSON.stringify(gs));
    let obj = updated;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!obj[keys[i]]) obj[keys[i]] = {};
      obj = obj[keys[i]];
    }
    obj[keys[keys.length - 1]] = value;
    saveSettings({ game_settings: updated });
  };

  const renderGames = () => (
    <div className="space-y-4">
      {/* Quiz Language */}
      <div className={`${cardClass} p-6`}>
        <h3 className={`text-lg font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Quiz Language</h3>
        <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Users will be quizzed in this language. Choose the language they are learning.</p>
        <select className={inputClass} value={gs.game_language || 'darija_latin'}
          onChange={e => updateGameSettings('game_language', e.target.value)}>
          {(settings?.languages || []).map(lang => (
            <option key={lang.code} value={lang.code}>{lang.name} ({lang.code})</option>
          ))}
        </select>
      </div>

      {/* Match the Label */}
      <div className={`${cardClass} p-6`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Match the Label</h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={gs.match_game?.enabled !== false}
              onChange={e => updateGameSettings('match_game.enabled', e.target.checked)} className="w-4 h-4 rounded" />
            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Enabled</span>
          </label>
        </div>
        <div className="space-y-3">
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Game Name</label>
            <input className={inputClass} value={gs.match_game?.name || ''} onChange={e => updateGameSettings('match_game.name', e.target.value)} />
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Question Text</label>
            <input className={inputClass} value={gs.match_game?.question_text || ''} onChange={e => updateGameSettings('match_game.question_text', e.target.value)} />
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Number of Options</label>
            <input type="number" min={2} max={6} className={inputClass} value={gs.match_game?.num_options || 4}
              onChange={e => updateGameSettings('match_game.num_options', parseInt(e.target.value) || 4)} />
          </div>
        </div>
      </div>

      {/* Is This Correct? */}
      <div className={`${cardClass} p-6`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Is This Correct?</h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={gs.correct_game?.enabled !== false}
              onChange={e => updateGameSettings('correct_game.enabled', e.target.checked)} className="w-4 h-4 rounded" />
            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Enabled</span>
          </label>
        </div>
        <div className="space-y-3">
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Game Name</label>
            <input className={inputClass} value={gs.correct_game?.name || ''} onChange={e => updateGameSettings('correct_game.name', e.target.value)} />
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Question Text</label>
            <input className={inputClass} value={gs.correct_game?.question_text || ''} onChange={e => updateGameSettings('correct_game.question_text', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Yes Button</label>
              <input className={inputClass} value={gs.correct_game?.yes_text || ''} onChange={e => updateGameSettings('correct_game.yes_text', e.target.value)} />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No Button</label>
              <input className={inputClass} value={gs.correct_game?.no_text || ''} onChange={e => updateGameSettings('correct_game.no_text', e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      {/* Points */}
      <div className={`${cardClass} p-6`}>
        <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Points</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Points per Correct</label>
            <input type="number" min={0} className={inputClass} value={gs.points_per_correct ?? 10}
              onChange={e => updateGameSettings('points_per_correct', parseInt(e.target.value) || 0)} />
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Bonus for 100%</label>
            <input type="number" min={0} className={inputClass} value={gs.bonus_perfect ?? 50}
              onChange={e => updateGameSettings('bonus_perfect', parseInt(e.target.value) || 0)} />
          </div>
        </div>
      </div>

      {/* Result Messages */}
      <div className={`${cardClass} p-6`}>
        <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Result Messages</h3>
        <div className="space-y-4">
          {['high', 'mid', 'low'].map(tier => (
            <div key={tier} className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
              <p className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                {tier === 'high' ? 'High Score' : tier === 'mid' ? 'Medium Score' : 'Low Score'}
              </p>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className={`block text-[10px] mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Emoji</label>
                  <input className={inputClass} value={gs.results?.[tier]?.emoji || ''} onChange={e => updateGameSettings(`results.${tier}.emoji`, e.target.value)} />
                </div>
                <div className={tier !== 'low' ? '' : 'col-span-2'}>
                  <label className={`block text-[10px] mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Message</label>
                  <input className={inputClass} value={gs.results?.[tier]?.message || ''} onChange={e => updateGameSettings(`results.${tier}.message`, e.target.value)} />
                </div>
                {tier !== 'low' && (
                  <div>
                    <label className={`block text-[10px] mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Threshold %</label>
                    <input type="number" min={0} max={100} className={inputClass} value={gs.results?.[tier]?.threshold ?? (tier === 'high' ? 80 : 50)}
                      onChange={e => updateGameSettings(`results.${tier}.threshold`, parseInt(e.target.value) || 0)} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* UI Customization */}
      <div className={`${cardClass} p-6`}>
        <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>UI Customization</h3>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Progress Icon</label>
              <input className={inputClass} value={gs.ui?.progress_icon || ''} onChange={e => updateGameSettings('ui.progress_icon', e.target.value)} />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Progress Title</label>
              <input className={inputClass} value={gs.ui?.progress_title || ''} onChange={e => updateGameSettings('ui.progress_title', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Journey Button Icon</label>
              <input className={inputClass} value={gs.ui?.journey_button_icon || ''} onChange={e => updateGameSettings('ui.journey_button_icon', e.target.value)} />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Journey Button Text</label>
              <input className={inputClass} value={gs.ui?.journey_button_text || ''} onChange={e => updateGameSettings('ui.journey_button_text', e.target.value)} />
            </div>
          </div>
          <div className="space-y-2 mt-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={gs.ui?.show_streak !== false}
                onChange={e => updateGameSettings('ui.show_streak', e.target.checked)} className="w-4 h-4 rounded" />
              <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Show Streak Counter</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={gs.ui?.show_timer_bar !== false}
                onChange={e => updateGameSettings('ui.show_timer_bar', e.target.checked)} className="w-4 h-4 rounded" />
              <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Show Timer Bar</span>
            </label>
            {gs.ui?.show_timer_bar !== false && (
              <div className="ml-6">
                <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Timer Duration (seconds)</label>
                <input type="number" min={5} max={60} className={`${inputClass} w-32`} value={gs.ui?.timer_seconds ?? 15}
                  onChange={e => updateGameSettings('ui.timer_seconds', parseInt(e.target.value) || 15)} />
              </div>
            )}
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={gs.ui?.show_score_popup !== false}
                onChange={e => updateGameSettings('ui.show_score_popup', e.target.checked)} className="w-4 h-4 rounded" />
              <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Show Score Popup (+10 animation)</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderHowToPlay = () => (
    <div className="space-y-4">
      <div className={`${cardClass} p-6`}>
        <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>How to Play Instructions</h3>
        <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          These steps are shown to users when they click "How to Play" in Scene Explorer.
        </p>

        {howToPlaySteps && (
          <div className="space-y-4">
            {howToPlaySteps.map((step, i) => (
              <div key={i} className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-bold ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>Step {i + 1}</span>
                  {howToPlaySteps.length > 1 && (
                    <button onClick={() => removeStep(i)}
                      className="text-xs text-red-500 hover:text-red-400 font-semibold">Remove</button>
                  )}
                </div>
                <input className={`${inputClass} mb-2`} value={step.title}
                  onChange={e => updateStep(i, 'title', e.target.value)}
                  placeholder="Step title..." />
                <textarea className={inputClass} rows={2} value={step.description}
                  onChange={e => updateStep(i, 'description', e.target.value)}
                  placeholder="Step description..." />
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-3 mt-4">
          <button onClick={addStep}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
            + Add Step
          </button>
          <button onClick={saveHowToPlay}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition-all">
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'settings', label: 'Settings', icon: '⚙️' },
    { id: 'categories', label: 'Categories', icon: '📂' },
    { id: 'languages', label: 'Languages', icon: '🌍' },
    { id: 'scenes', label: 'Scenes', icon: '🖼️' },
    { id: 'games', label: 'Games', icon: '🎯' },
    { id: 'howtoplay', label: 'How to Play', icon: '🎮' }
  ];

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              activeTab === tab.id
                ? 'bg-purple-600 text-white'
                : isDarkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {loading ? (
        <div className="text-center py-12">
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Loading...</p>
        </div>
      ) : (
        <>
          {activeTab === 'settings' && renderSettings()}
          {activeTab === 'categories' && renderCategories()}
          {activeTab === 'languages' && renderLanguages()}
          {activeTab === 'scenes' && renderScenesList()}
          {activeTab === 'games' && renderGames()}
          {activeTab === 'howtoplay' && renderHowToPlay()}
        </>
      )}

      {/* Modals */}
      {showSceneModal && <SceneModal />}
      {showObjectModal && <ObjectModal />}

      {/* Reset Progress Confirmation Modal */}
      {resetConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setResetConfirm(null)}>
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 w-full max-w-sm text-center`} onClick={e => e.stopPropagation()}>
            <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${
              isDarkMode ? 'bg-orange-900/40' : 'bg-orange-50'
            }`}>
              <span className="text-3xl">&#9888;&#65039;</span>
            </div>
            <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Reset All Progress?
            </h3>
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              This will delete <strong className={isDarkMode ? 'text-white' : 'text-gray-800'}>ALL users'</strong> progress for <strong className={isDarkMode ? 'text-white' : 'text-gray-800'}>{resetConfirm.sceneName}</strong>.
            </p>
            {resetConfirm.dependentScenes?.length > 0 && (
              <div className={`rounded-xl p-3 mb-4 text-left ${isDarkMode ? 'bg-orange-900/30 border border-orange-700/40' : 'bg-orange-50 border border-orange-100'}`}>
                <p className={`text-xs font-semibold mb-1.5 ${isDarkMode ? 'text-orange-300' : 'text-orange-600'}`}>
                  These scenes will lock for everyone:
                </p>
                {resetConfirm.dependentScenes.map(s => (
                  <p key={s.id} className={`text-xs ${isDarkMode ? 'text-orange-200' : 'text-orange-700'}`}>
                    • {s.name}
                  </p>
                ))}
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => setResetConfirm(null)}
                className={`flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                  isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}>
                Cancel
              </button>
              <button onClick={async () => {
                try {
                  const res = await fetch(`${API}/admin/scene-explorer/scenes/${resetConfirm.sceneId}/all-progress`, { method: 'DELETE', headers });
                  if (res.ok) {
                    const data = await res.json();
                    showNotification(`Progress reset for ${data.deleted_count} user(s)`, 'success');
                    setResetConfirm(null);
                    loadScenes();
                  }
                } catch (err) { showNotification('Error resetting progress', 'error'); }
              }}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold text-sm transition-all">
                Reset All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generic Confirmation Modal */}
      {confirmModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setConfirmModal(null)}>
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 w-full max-w-sm text-center`} onClick={e => e.stopPropagation()}>
            <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${
              confirmModal.confirmColor === 'red'
                ? (isDarkMode ? 'bg-red-900/40' : 'bg-red-50')
                : (isDarkMode ? 'bg-orange-900/40' : 'bg-orange-50')
            }`}>
              <span className="text-3xl">{confirmModal.confirmColor === 'red' ? '🗑️' : '⚠️'}</span>
            </div>
            <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {confirmModal.title}
            </h3>
            <p className={`text-sm mb-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {confirmModal.message}
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmModal(null)}
                className={`flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                  isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}>
                Cancel
              </button>
              <button onClick={confirmModal.onConfirm}
                className={`flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                  confirmModal.confirmColor === 'red'
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-orange-600 hover:bg-orange-700 text-white'
                }`}>
                {confirmModal.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SceneExplorerAdmin;
